import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, Heart, Calendar, DollarSign } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon registry — maps backend `icon` string to Lucide component
const ICON_MAP = {
  AlertCircle: <AlertCircle className="h-4 w-4" />,
  Heart: <Heart className="h-4 w-4" />,
  Calendar: <Calendar className="h-4 w-4" />,
  DollarSign: <DollarSign className="h-4 w-4" />,
};

// Hardcoded fallback if API is unreachable or empty
const FALLBACK_MESSAGES = [
  {
    id: 'fallback-1',
    type: 'affirmation',
    icon: 'Heart',
    message: '🌟 You are capable. You are resilient. Your business matters.',
    color: 'bg-[#006847]',
    link: null,
  },
  {
    id: 'fallback-2',
    type: 'event',
    icon: 'Calendar',
    message: '🤖 Try Grant Concierge AI — find grants, draft proposals, track compliance.',
    color: 'bg-[#1E4D2E]',
    link: '/grant-concierge',
  },
];

function TickerFeed() {
  const [messages, setMessages] = useState(FALLBACK_MESSAGES);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  // Fetch active messages from CMS
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/ticker/active`)
      .then((res) => {
        if (cancelled) return;
        const apiMessages = res.data?.messages || [];
        if (apiMessages.length > 0) setMessages(apiMessages);
      })
      .catch(() => {
        // Keep fallback
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('tickerDismissed');
    if (dismissed) {
      setIsDismissed(true);
      return;
    }
    if (messages.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % messages.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [messages.length]);

  // Reset index if messages list shrinks
  useEffect(() => {
    if (currentIndex >= messages.length) setCurrentIndex(0);
  }, [messages.length, currentIndex]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => setIsDismissed(true), 300);
    sessionStorage.setItem('tickerDismissed', 'true');
  };

  if (isDismissed || messages.length === 0) return null;

  const currentMessage = messages[currentIndex];
  const iconNode = ICON_MAP[currentMessage.icon] || <Heart className="h-4 w-4" />;

  const handleLinkClick = () => {
    // Fire-and-forget click tracking
    if (currentMessage.id) {
      axios.post(`${API}/api/ticker/click/${currentMessage.id}`).catch(() => { /* silent */ });
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={`fixed top-16 left-0 right-0 z-50 ${currentMessage.color} text-white shadow-md`}
          data-testid="ticker-banner"
        >
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <div className="flex-shrink-0">
                  {iconNode}
                </div>
                <AnimatePresence mode="wait">
                  <motion.p
                    key={currentMessage.id || currentIndex}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="text-xs md:text-sm font-medium truncate"
                  >
                    {currentMessage.message}
                  </motion.p>
                </AnimatePresence>
              </div>

              <div className="flex items-center space-x-2 flex-shrink-0">
                {currentMessage.link && (
                  <a
                    href={currentMessage.link}
                    target={currentMessage.link.startsWith('http') ? '_blank' : '_self'}
                    rel="noopener noreferrer"
                    onClick={handleLinkClick}
                    className="text-xs underline hover:no-underline whitespace-nowrap hidden sm:inline"
                  >
                    More →
                  </a>
                )}
                <button
                  onClick={handleDismiss}
                  className="p-0.5 hover:bg-white/20 rounded transition"
                  title="Dismiss"
                  data-testid="ticker-dismiss-btn"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center space-x-1 pb-1">
            {messages.map((m, idx) => (
              <div
                key={m.id || `dot-${idx}`}
                className={`w-1 h-1 rounded-full transition-all ${
                  idx === currentIndex ? 'bg-white w-3' : 'bg-white/40'
                }`}
              />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default TickerFeed;
